import React from 'react';
import { PRIVACY_PATH, TERMS_PATH } from '../utils/publicPolicyPaths';

interface PolicyPageShellProps {
  title: string;
  /**
   * Version/effective-date of the published document being shown (task 441).
   * Null while the document is loading or failed to load — the meta line is
   * simply omitted then.
   */
  meta?: { version: string; effectiveAt: string } | null;
  children: React.ReactNode;
}

/** Render the ISO effectiveAt timestamp as its date part (YYYY-MM-DD). */
function formatEffectiveDate(effectiveAt: string): string {
  return effectiveAt.slice(0, 10);
}

/**
 * Shared shell for the public policy pages (`/privacy`, `/terms`, task 422).
 *
 * These pages render OUTSIDE the authenticated app shell (no AppLayout, no
 * auth), so the shell provides its own minimal header/footer, themed to match
 * the app (Tailwind + dark mode via ThemeProvider, which App.tsx mounts above
 * the public branch). Links use plain anchors — there is no Router on the
 * public branch.
 *
 * Since task 441 the pages render the current PUBLISHED document fetched from
 * AMS (via the innovate-service proxy), so the shell shows the live
 * version/effective date instead of the retired DRAFT banner.
 */
export default function PolicyPageShell({ title, meta = null, children }: PolicyPageShellProps) {
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <header className="border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-950">
        <div className="mx-auto max-w-3xl px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-sm font-semibold text-gray-900 dark:text-white">
            Monarch Works Innovate
          </a>
          <nav className="flex gap-4 text-sm">
            <a
              href={TERMS_PATH}
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Terms of Use
            </a>
            <a
              href={PRIVACY_PATH}
              className="text-indigo-600 dark:text-indigo-400 hover:underline"
            >
              Privacy Policy
            </a>
          </nav>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 py-10">
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">{title}</h1>
        {meta && (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Version {meta.version} — effective {formatEffectiveDate(meta.effectiveAt)}
          </p>
        )}

        <div className="mt-8 space-y-8 text-sm leading-6 text-gray-700 dark:text-gray-300">
          {children}
        </div>
      </main>

      <footer className="mx-auto max-w-3xl px-6 pb-10 text-xs text-gray-400 dark:text-gray-500">
        © {new Date().getFullYear()} Monarch AI, LLC. All rights reserved.
      </footer>
    </div>
  );
}
